import { useEffect, useState } from "react";
import { FormikProvider, useFormik, Form, Field, ErrorMessage } from "formik";
import * as bie from "react-bootstrap";
import * as Yup from "yup";
import { AllowFloats } from "../CommonUtils/CommonValidations";
import { config } from "../CommonUtils/CommonURLS";
import { CommonAxiosGet, CommonAxiosPost } from "../CommonUtils/CommonAxios";
import Swal from "sweetalert2";
import { FaEdit } from "react-icons/fa";
import { useRusaFormSubmit } from "./hooks/useRusaFormSubmit";
import { ERROR_MSG } from "../CommonUtils/contextVariables";
import { currentMonth, currentYear } from "../CommonUtils/CommonServices";
import CommonReactTable from "../CommonUtils/CommonReactTable";
import { RiDeleteBin2Line } from "react-icons/ri";


const ProjectStatus = () => {
  const { submitForm } = useRusaFormSubmit();

  // Local State
  const [component, setComponent] = useState([]);
  const [subcomponentdata, setSubcomponentdata] = useState([]);
  const [phasename, setPhasename] = useState([]);

  // API Calls specific to this component
  function GetComponentData(phase) {

    console.log("GetComponentData called with phase:", phase);
    if (!phase) return;
    const url = `${config.url.API_URL}getRusaComponentList?rusa_phase=${phase}`;
    CommonAxiosGet(url).then((res) => {
      setComponent(res?.data?.scode === "01" ? res.data.data : []);
    });

  }


  const GetSubComponent = () => {
    const url = `${config.url.API_URL}getRusaSubCompList`;
    CommonAxiosGet(url).then((res) => {
      setSubcomponentdata(res?.data?.scode === "01" ? res.data.data : []);
    });
  };

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const validationSchema = Yup.object({
    rusaPhase: Yup.string().required(ERROR_MSG).nullable(),
    compId: Yup.string().required(ERROR_MSG).nullable(),
    subCompId: Yup.string().required(ERROR_MSG).nullable(),
    amountReleased: Yup.string().required(ERROR_MSG).nullable(),

    amountUtilised: Yup.number()
      .typeError(ERROR_MSG)
      .required(ERROR_MSG).nullable()
      .when("amountReleased", (amountReleased, schema) =>
        schema.max(
          amountReleased,
          "Utilised amount cannot exceed the released amount"
        )
      ),
  })
  const formik = useFormik({
    initialValues: {
      rusaPhase: "",
      compId: "",
      subCompId: "",
      amountReleased: "",
      amountUtilised: "",
      balanceAmount: "",
    },
    validationSchema,


    onSubmit: (val) => {

      const payload = {
        rusaPhase: val.rusaPhase,
        compId: Number(val.compId),
        subCompId: Number(val.subCompId),
        approvedAmount: Number(val.amountReleased),
        utilizedAmount: Number(val.amountUtilised),
        balanceAmount: Number(val.balanceAmount),
        month: currentMonth,
        year: currentYear,
        // ...(isEdit && { id: editId })
      };
      // ✅ Decide API here
      const apiUrl = isEdit
        ? config.url.API_URL + "updateSchemeData?sno=" + editId
        : config.url.API_URL + "saveRusaUpdationData";

      // ✅ Decide confirmation text here
      console.log("payload", payload, apiUrl)

      const confirmText = isEdit
        ? "Are you sure you want to update?"
        : "Are you sure you want to submit?";

      submitForm(payload, () => {
        GetProjectStatusData();
        formik.resetForm();
        setIsEdit(false);
        setEditId(null);
      }, apiUrl, confirmText);
    }



  });

  // Auto Balance Calculation
  useEffect(() => {
    const released = Number(formik.values.amountReleased || '');
    const utilised = Number(formik.values.amountUtilised || '');
    formik.setFieldValue("balanceAmount", released - utilised);
  }, [formik.values.amountReleased, formik.values.amountUtilised]);

  const ReportColumns = [
    {
      id: "slNo",
      header: "Sl.No",
      cell: ({ row }) => row.index + 1,
    },
    {
      id: "rusa_phase",
      header: "RUSA Phase",
      accessorKey: "rusa_phase",
    },
    {
      id: "component_name",
      header: "Component Name",
      accessorKey: "component_name",
    },
    {
      id: "sub_comp_name",
      header: "Sub Component Name",
      accessorKey: "sub_comp_name",
    },
    {
      id: "amount_allocated",
      header: "Amount Allocated (₹)",
      accessorKey: "amount_allocated",
      cell: ({ getValue }) =>
        Number(getValue() || 0).toLocaleString("en-IN"),
    },
    {
      id: "amount_utilised",
      header: "Amount Utilised (₹)",
      accessorKey: "amount_utilised",
      cell: ({ getValue }) =>
        Number(getValue() || 0).toLocaleString("en-IN"),
    },
    {
      id: "balance_amount",
      header: "Balance Amount (₹)",
      accessorKey: "balance_amount",

    },

    {
      id: "actions",
      header: "Edit / Delete",
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                formik.setValues({
                  rusaPhase: rowData?.rusa_phase || "",
                  compId: rowData?.comp_id || "",
                  subCompId: rowData?.sub_comp_id || "",
                  amountReleased: rowData?.amount_allocated || "",
                  amountUtilised: rowData?.amount_utilised || "",
                  balanceAmount: rowData?.balance_amount || "",

                });

                GetComponentData(rowData?.rusa_phase);
                setIsEdit(true);           // 🔹 Enable edit mode
                setEditId(rowData?.sno);
              }}

            >
              <FaEdit size={16} />
            </button>

            {/* 🗑️ DELETE */}
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => {
                Swal.fire({
                  title: "Are you sure?",
                  text: "Do you want to delete this record?",
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonText: "Yes, delete it!",
                }).then((result) => {
                  if (result.isConfirmed) {
                    const url = `${config.url.API_URL}deleteSchemeData?sno=${rowData.sno}`;

                    CommonAxiosPost(url)
                      .then((res) => {
                        if (res?.data?.scode === "01") {
                          Swal.fire(res?.data?.sdesc, "", "success");
                          GetProjectStatusData();
                        } else {
                          Swal.fire("Failed to Delete", "", "error");
                        }
                      })

                  }
                });
              }}
            >
              <RiDeleteBin2Line

                size={20} />
            </button>
          </div>
        );
      },
    },
  ];
  function GetProjectScheme() {
    const url = `${config.url.API_URL}getPhase`;
    CommonAxiosGet(url).then((res) => {
      setPhasename(res?.data?.scode === "01" ? res.data.phase : []);
    });
  }
  const [projectdata, serProjectdata] = useState([]);
  function GetProjectStatusData() {
    let url = `${config.url.API_URL}getSchemeCompDetails?month=${currentMonth}&year=${currentYear}`;
    CommonAxiosGet(url).then((res) => {
      if (res?.data?.scode === "01" &&
        res?.data?.data?.length > 0) {
        serProjectdata(res?.data?.data);
      } else {
        serProjectdata([]);
      }
    });
  }
  useEffect(() => {
    GetProjectScheme();
    GetProjectStatusData();
    GetSubComponent();

  }, [])


  return (
    <div className="pt-3">
      <div className="overal-stathead">
        <span>Status of the Project</span>
      </div>

      <FormikProvider value={formik}>
        <Form onChange={formik?.onSubmit} onSubmit={formik?.handleSubmit}>
          <bie.Row className="g-3">
            <bie.Col lg={4}>
              <label>Scheme</label>
              <Field as="select" name="rusaPhase" className="form-control"
                onChange={(e) => {
                  formik.handleChange(e);        // ✅ update Formik state
                  GetComponentData(e.target.value); // ✅ your custom logic
                }}
              >
                <option value="">--select--</option>
                {phasename?.map((p) => (
                  <option key={p.rusa_phase} value={p.rusa_phase}>
                    {p.rusa_phase}
                  </option>
                ))}


              </Field>

              <ErrorMessage name="rusaPhase" component="div" className="text-danger" />
            </bie.Col>

            {/* Component */}
            <bie.Col lg={4}>
              <label>Component</label>
              <Field as="select" name="compId" className="form-control" >
                <option value="">--select--</option>

                {component?.map((c) => (
                  <option key={c.comp_id} value={c.comp_id}>
                    {c.component_name}
                  </option>
                ))}

              </Field>
              <ErrorMessage name="compId" component="div" className="text-danger" />
            </bie.Col>

            {/* Sub Component */}
            <bie.Col lg={4}>
              <label>Sub Components</label>
              <Field as="select" name="subCompId" className="form-control">
                <option value="">--select--</option>

                {subcomponentdata.length > 0 ? (
                  subcomponentdata.map((s) => (
                    <option key={s.sub_comp_id} value={s.sub_comp_id}>
                      {s.sub_comp_name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    No Data Found
                  </option>
                )}

              </Field>
              <ErrorMessage name="subCompId" component="div" className="text-danger" />
            </bie.Col>

            {/* {formik.values.subCompId && (
              <> */}
            <bie.Col lg={4}>
              <label>Amount Allocated</label>
              <Field
                name="amountReleased"
                className="form-control"
                onKeyPress={AllowFloats}
              />
              <ErrorMessage name="amountReleased" component="div" className="text-danger" />
            </bie.Col>

            <bie.Col lg={4}>
              <label>Amount Utilised</label>
              <Field
                name="amountUtilised"
                className="form-control"
                onKeyPress={AllowFloats}
              />
              <ErrorMessage name="amountUtilised" component="div" className="text-danger" />
            </bie.Col>

            <bie.Col lg={4}>
              <label>Balance Amount</label>
              <Field
                name="balanceAmount"
                className="form-control"
                readOnly
              />
            </bie.Col>
            {/* </>
            )} */}
          </bie.Row>

          <div className="text-end mt-4 mb-5">
            <button type="submit" className="btn btn-success">
              {isEdit ? "Update" : "Submit"}
            </button>

          </div>


          {projectdata?.length > 0 && (
            <>

              <div className="overal-stathead">
                <span>Project Status  Report</span>
              </div>
              <div className="all-table-content">
                <div className="table-responsive mt--40">
                  <CommonReactTable
                    data={projectdata}
                    columns={ReportColumns}
                    filename="Project Status Details Report"
                    headerName="Project Status Details Report"
                    showFooter={true}
                    showPagination={false}

                  />
                </div>
              </div>
            </>
          )}
        </Form>
      </FormikProvider >
    </div >
  );
};

export default ProjectStatus;
